// ============================================================================
// TYPE DEFINITIONS
// Core types for the Adaptive Endurance application
// ============================================================================

// ============================================================================
// PHASE & BLOCK TYPES
// ============================================================================

export type PhaseType = 'base' | 'build' | 'peak' | 'taper' | 'race' | 'recovery' | 'transition' | 'pre_build';

export type SportType = 'running' | 'cycling' | 'triathlon' | 'swimming' | 'longevity';


import type { GamificationState } from '@/lib/gamification/types';
import type {
    PhilosophyArchetype,
    RecoveryTrigger,
    RtssTier,
    WeekRulesTrace,
} from '@/lib/scheduler/scheduler-rules';

// ============================================================================
// TRIATHLON & DISCIPLINE TYPES
// ============================================================================

/**
 * Discipline types for multi-sport training.
 * Used for workout tagging and load distribution.
 */
export type DisciplineType = 'swimming' | 'cycling' | 'running' | 'strength' | 'brick';

/**
 * Standard triathlon race distances with typical parameters.
 */
export type TriathlonDistance =
    | 'super_sprint'   // 400m swim, 10km bike, 2.5km run
    | 'sprint'         // 750m swim, 20km bike, 5km run
    | 'olympic'        // 1.5km swim, 40km bike, 10km run
    | 'half_ironman'   // 1.9km swim, 90km bike, 21.1km run
    | 'full_ironman';  // 3.8km swim, 180km bike, 42.2km run

/**
 * Triathlon distance specifications.
 */
export interface TriathlonDistanceSpec {
    swim: number;      // meters
    bike: number;      // km
    run: number;       // km
    typicalHoursMin: number;
    typicalHoursMax: number;
    weeklyHoursMin: number;
    weeklyHoursMax: number;
}

/**
 * ECO (Equivalent Training Stress) multipliers for cross-sport load normalization.
 * Running is the baseline (1.0) due to high eccentric load and impact stress.
 */
export const ECO_MULTIPLIERS: Record<DisciplineType, number> = {
    running: 1.0,      // High eccentric load, impact stress
    swimming: 0.75,    // Zero impact, technique-dependent, hypoxic stress
    cycling: 0.5,      // Concentric-only, low muscle damage
    strength: 0.3,     // Minimal aerobic stress
    brick: 1.0,        // Combined - calculated separately
} as const;

/**
 * Feature flags for enabling/disabling sports.
 * Allows staged rollout of triathlon functionality.
 */
export interface EnabledSports {
    running: boolean;
    cycling: boolean;
    swimming: boolean;
    triathlon: boolean;
    strava: boolean;
}

export interface PlanSettings {
    progressionRate: number;
    taperDuration: number;
    startWithRestWeek: boolean;
    volumeMultiplier: number;
    skipOnboardingRamp?: boolean;
    planningMethod?: 'volume' | 'tss' | 'ctl';
    planningStrategy?: 'performance' | 'time_efficiency' | 'foundation' | 'event_target';
    timeCrunchedIntensityMode?: boolean;
    hrvReadinessSuggestionsEnabled?: boolean;
    strictIntensityDistribution?: boolean;
    philosophyOverride?: PhilosophyArchetype;
    ambitionLevel?: 'finish' | 'compete' | 'podium';
    eventTargetAmbitionLevel?: 'finish' | 'top_50' | 'top_20';
    eventTargetDurationHours?: number;
    eventTargetWeeksToEvent?: number;
    cyclePattern?: '2:1' | '3:1' | '4:1' | 'custom';
    limiterFocus?: 'endurance' | 'force' | 'speed' | 'anaerobic' | 'technique' | null;
    useEffectiveCap?: boolean;
    useCyclicPlateau?: boolean;
    currentCTL?: number;
    targetCTL?: number;
    enableUnsafePlanning?: boolean;
    useDurationTargeting?: boolean; // SCI-01: Toggle for hour-based targets
    maxBigRocksPerWeek?: number;   // SCI-02: User cap for key sessions
    minRunDuration?: number;       // Everyday Friendly: Configurable minimum session length (minutes)
    optionalStrength?: boolean;    // FEAT: Include recommended optional strength sessions
    disableMastersModifiers?: boolean; // Masters athletes can opt out of age-based safety adjustments
    _syncedData?: {
        featureFlags?: Record<string, FeatureFlag>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
}

export type LimiterType =
    | 'none'           // No limiter active (TSS < 350 or unconstrained)
    | 'post_recovery'  // Post-recovery week capped at pre-recovery TSS
    | 'time_cap'       // Standard time ceiling
    | 'time_cap_bonus' // Time ceiling + 10% bonus (motivated athlete)
    | 'acwr'           // Acute:Chronic Workload Ratio limit
    | 'recovery'       // Recovery week (special case)
    | 'taper'          // Taper phase (special case)
    | 'race'           // Race week (special case)
    | 'peak'           // Peak phase (special case)
    | 'ramp_rate'      // Ramp rate limit
    | 'volume_governor' // Absolute volume governor
    | 'ACUTE_CHRONIC_SPIKE_HARD'
    | 'ACUTE_CHRONIC_SPIKE_CHRONIC'
    | 'CTL_RAMP_SINGLE_WEEK_EXCEEDED'
    | 'CTL_RAMP_ROLLING_AVG_EXCEEDED';

/** ACWR Traffic Light zones for visualization */
export type ACWRZone = 'green' | 'yellow' | 'red' | 'gray';

/** Safety intervention record for audit logging */
export interface SafetyIntervention {
    limiterActive: LimiterType;
    proposedTSS: number;        // What the engine wanted
    actualTSS: number;          // What was applied
    rationale: string;          // Human-readable explanation
    acwrValue?: number;         // ACWR at time of intervention
    acwrThreshold?: number;     // Threshold that triggered it
    rampPercent?: number;       // Growth % that was applied
}

export interface PhaseConfig {
    type: PhaseType;
    name: string;          // e.g., "Base 1"
    description: string;
    focus: string[];       // e.g., ["Aerobic Endurance", "Strength"]
    durationWeeks: number; // Planned duration
}

export interface TrainingBlock {
    id: string;
    name: string;          // e.g., "Base Block 1"
    type: PhaseType;
    startDate: string;
    durationWeeks: number;
    startVolume: number;   // Planned volume for start of block
    endVolume: number;     // Planned volume for end of block
    focus: string[];       // e.g. ['Aerobic', 'Strength']
}

// ============================================================================
// WORKOUT TYPES
// ============================================================================

export type Zone = 'Z1' | 'Z2' | 'Z3' | 'Z4' | 'Z5a' | 'Z5b' | 'Z5c';

export interface WorkoutSegment {
    duration?: number;     // Minutes
    distance?: number;     // Meters
    zone: Zone | string;   // Allowing string for flexibility
    percent?: number;      // For progressive segments
    press_lap?: boolean;   // Mark lap on watch
    // Power specific
    powerTarget?: number; // Specific Watts
    powerZone?: Zone | string;     // Override zone for power (if different from Pace zone)
    powerPct?: [number, number]; // Percentage range for cycling [min, max]
    cadence?: number | [number, number]; // RPM target or range
    rpe?: number | [number, number]; // Rate of Perceived Exertion
    description?: string; // Optional segment description
    /** SCI-WE-05: Explicit type to distinguish between rest (intra-set) and recovery (inter-set/standalone) */
    type?: 'warmup' | 'work' | 'rest' | 'recovery' | 'cooldown';
    name?: string;         // Optional display name for the segment
}

export interface IntervalSet {
    reps: number;
    work: WorkoutSegment | WorkoutSegment[];
    rest: WorkoutSegment | WorkoutSegment[];
    name?: string;         // Optional name for the set (e.g., "Set 1: VO2 Max")
}

export interface WorkoutStructure {
    warmup?: WorkoutSegment;
    main: WorkoutSegment | IntervalSet | { segments: (WorkoutSegment | IntervalSet)[] };
    cooldown?: WorkoutSegment;
}

export interface WorkoutStep {
    type: 'warmup' | 'work' | 'recovery' | 'cooldown' | 'rest' | 'interval';
    duration: number | string; // e.g., 600 (seconds) or "10m", "400m"
    intensity: string;     // e.g., "60-70%", "Z2", "95-100% Pace"
    cadence?: number | [number, number];      // For cycling (target or range)
    notes?: string;
    name?: string;         // Added for custom step names (e.g. Strides)
    description?: string;  // Added for detailed step instructions
    power?: {              // Added for Cycling
        zone: string;      // e.g. "Z2"
        watts?: number;    // e.g. 200
        percentFTP?: number; // e.g. 70
        percentCP?: number; // Added for Running Power (SCI-04)
    };
    powerTarget?: number; // Explicit target in Watts (generic)
    powerPct?: [number, number]; // Percentage range for cycling [min, max]
    rpe?: number | [number, number]; // Rate of Perceived Exertion
    distance?: number; // Distance in meters
    press_lap?: boolean;
    reps?: number;
    steps?: WorkoutStep[];
}

export type WorkoutSport = 'Running' | 'Cycling' | 'Swimming' | 'Triathlon' | 'Strength' | 'Brick';

export interface Workout {
    id: string;            // e.g., "RUN_04"
    name: string;          // e.g., "Cruise Intervals"
    sport: WorkoutSport;
    focus: string;         // e.g., "Lactate Threshold"
    progressionRule: string;
    steps: WorkoutStep[];
    estimatedDuration: number;  // minutes
    estimatedDistance?: number; // km
    estimatedTSS?: number;      // Added for Cycling
    color?: string;        // Hex color for UI
    blueprintId?: string;  // Original library/template ID
    description?: string;  // Added for workout description
    notes?: string;        // Critical notes (e.g. nutrition, pacing)
    powerStructure?: WorkoutStructure; // SCI-04: Power-based structure
    progressionLevel?: number; // Added to fix build error (Everyday Athlete logic)

    // === Educational Guide Fields (Methodology Insights) ===
    /** Coach's explanation of why this workout matters */
    coachPurpose?: string;
    /** Single focus cue for the athlete to remember during the session */
    focusCue?: string;
    /** Execution tips for performing the workout correctly */
    executionTips?: string[];
    /** Common mistakes to avoid during this type of workout */
    commonMistakes?: string[];
    /** Dynamic rationale for why this workout is scheduled here (Methodology) */
    rationale?: string;
}


export interface ScheduledWorkout extends Workout {
    instanceId: string;    // Unique UUID for this specific instance of the workout
    date: string;          // ISO date string
    dayOfWeek: number;     // 0-6 (Sun-Sat)
    slot: 'am' | 'pm';
    weekNumber: number;
    isCompleted?: boolean;
    isRace?: boolean;      // Added for Scheduler logic
    actualDuration?: number;
    plannedTSS?: number;        // Added for Cycling
    actualTSS?: number;         // Added for Cycling
    tss?: number;               // General TSS field
    distance?: number;          // Added if estimatedDistance is not enough
    options?: ScheduledWorkout[]; // Alternatives for day slot
    progressionLevel?: number;   // Added to fix build error (Everyday Athlete logic)
    longSessionType?: 'steady' | 'structured'; // For structured long run/ride tracking
    completionStatus?: 'planned' | 'done' | 'missed' | 'skipped_sick' | 'skipped_life';
    completedAt?: string; // ISO timestamp
    rpe?: number;          // Rate of Perceived Exertion (1-10)
    feeling?: number;      // Feeling (1-5)
    isSafetyCapped?: boolean; // Flag to indicate if a safety limit was enforced (e.g. CTL target)
}

/**
 * Pairs planned workouts with actual logged activities
 */
export interface WorkoutPair {
    date: string;
    planned?: ScheduledWorkout;
    actual?: {
        id: string;
        name: string;
        date: string;
        type: string;
        distance: number;
        duration: number;
        tss?: number;
        color: string;
    };
}

// ============================================================================
// SCHEDULE TYPES
// ============================================================================

export interface DaySlot {
    date: string; // ISO date string
    workout: ScheduledWorkout | null;
    secondary: ScheduledWorkout | null; // PM workout
    priority?: 'KEY' | 'BIG_ROCK' | 'FILLER' | 'FILL' | 'RECOVERY' | 'BLOCKED' | 'SUPPORT'; // Added to fix build error
    distance?: number; // Added to fix build error
    isRest?: boolean;
    availableHours?: number;
}

export interface WeekSchedule {
    weekNumber: number;
    startDate: string;
    endDate: string;
    phase: PhaseType;
    isRecoveryWeek?: boolean; // Added for convenience and checking logic
    blockId?: string;      // Link to parent TrainingBlock
    targetVolume: number;  // km or hours
    targetTSS?: number;    // Added for Cycling (Stress Score)
    actualVolume?: number;
    actualTSS?: number;
    actualDuration?: number;
    days: DaySlot[];       // 7 elements (Sun-Sat)
    notes?: string;
    capacity?: number;     // The underlying volume capacity of the athlete this week (before phase reductions)
    targetDuration?: number; // Added for Cycling (minutes)
    recoveryTrigger?: RecoveryTrigger;
    philosophy?: PhilosophyArchetype;
    rulesTrace?: WeekRulesTrace;

    // Compatibility with MacroWeek
    volume?: number;
    longRun?: number;
    isRecovery?: boolean;
    focus?: string;
    weeksToRace?: number;

    // Manual Overrides
    availabilityOverride?: WeeklyAvailability;
    longRunDayOverride?: number;
    volumeOverride?: number; // Override target volume
    maxWeeklyHoursOverride?: number; // Override max weekly hours
    raceTypeOverride?: string;
    longRunDistance?: number; // Manual override or planned distance
    warning?: string;         // Legacy ATP warning or health alert
    schedulerWarnings?: string[]; // Weekly placement notes from the scheduler (not ATP health)
    flags?: string[];         // Hard ATP validation findings
    concerns?: string[];      // Soft ATP validation findings
    suggestions?: string[];   // ATP improvement opportunities
    isAtCap?: boolean;        // Whether the macro engine hit a safe capacity ceiling
    phaseSource?: 'proposed' | 'user_set';
    locked?: boolean;

    // Pro View Metrics (ATP-UI Enhancement)
    intensityFactor?: number; // IF or rIF
    ctl?: number;             // Chronic Training Load (Fitness)
    tsb?: number;             // Training Stress Balance (Form)
    rTSS?: number;            // Running TSS
    acRatio?: number;         // Acute:Chronic Ratio
    acwrValue?: number;       // Value for Traffic Light component (Standardized)
    activeLimiter?: LimiterType; // Which TSS limiter was active (ATP-UI)
    rtssTier?: RtssTier;

    // New Pro View Metrics
    startingCTL?: number;
    endCTL?: number;
    endTSB?: number;
    endATL?: number;
    atl?: number;
    projectedMetrics?: {
        ctl: number;
        atl: number;
        tsb: number;
        startCTL?: number;
    };
    /** Rationale for scheduler decisions in this week */
    rationale?: string;
    /** Safety intervention details for educational tooltips */
    safetyIntervention?: SafetyIntervention;
    /** Type of long session in this week */
    longSessionType?: 'steady' | 'structured';
    /** Current week number within the training phase (e.g. Base Week 3) */
    phaseRelativeWeek?: number;
    /** Activities matched to this week for reporting */
    matchedActivities?: IntervalsActivity[];
    /** Triathlon-only explicit discipline targets carried from the ATP */
    triathlonTargets?: TriathlonWeeklyTargets;
}

export interface DayAvailability {
    hours: number;
    split: boolean;
    amHours: number;
    pmHours: number;
}

export type WeeklyAvailability = Record<number, DayAvailability>; // 0-6 (Sun-Sat)

// ============================================================================
// REFLOW TRIGGER TYPES
// ============================================================================

export type ReflowTriggerType =
    | 'date_change'    // Goal date moved, standard reflow
    | 'missed_weeks'   // Athlete missed training, volume decay
    | 'injury'         // Force rest, more aggressive volume reduction
    | 'vacation'       // Volume pause, maintain fitness
    | 'manual';        // User-initiated reflow

export interface ReflowTrigger {
    type: ReflowTriggerType;
    startDate: string;     // When the trigger event started
    endDate?: string;      // When the trigger event ended (optional for ongoing)
    durationWeeks?: number; // Duration of the event
    notes?: string;        // User notes
}

export interface ReflowOptions {
    trigger: ReflowTrigger;
    preserveHistory: boolean;      // Keep past weeks
    aggressiveDecay?: boolean;     // For injury: more aggressive volume reduction
    maintainFitness?: boolean;     // For vacation: assume cross-training maintained base
    targetResumeVolume?: number;   // Override calculated resume volume
    transitionWeeks?: number;      // Weeks to ramp back up (default: 2 for injury, 1 for vacation)
}

// ============================================================================
// ATHLETE TYPES
// ============================================================================

/**
 * Supported athlete sports.
 * Triathlon requires running AND cycling zones, plus swimming metrics.
 */
export type AthleteSport = 'Running' | 'Cycling' | 'Triathlon' | 'Longevity';

/**
 * Experience tier for training volume and intensity scaling.
 * New values: 'base' | 'regular' | 'performance' | 'competitive'
 * Legacy values (deprecated): 'fresh_start' | 'transfer' | 'consistent' | 'high_performance'
 */
export type ExperienceTier =
    | 'base' | 'regular' | 'performance' | 'competitive'  // New values
    | 'fresh_start' | 'transfer' | 'consistent' | 'high_performance';  // Legacy (deprecated)

export interface AthleteProfile {
    name: string;
    email?: string; // Added for User Identification
    sport: AthleteSport;
    experience: ExperienceTier;
    weight?: number;       // kg
    age?: number;
    birthdate?: string; // YYYY-MM-DD
    gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    height?: number; // cm
    gymAccess: 'no_gym' | 'gym';
    maxWeeklyTrainingHours?: number; // Cap on total weekly volume
    injuryReturn?: boolean;   // Safety guardrail: active during injury transition window
    injuryStartDate?: string;     // ISO date when the injury reflow began
    injuryRecoveredDate?: string; // ISO date when athlete marked themselves recovered
    thresholdPace?: number; // m/s (Functional Threshold Pace for rTSS)
    criticalPower?: number; // Watts (CP for Running Power)
    maxStrengthSessionsPerWeek: number; // Maximum strength sessions per week (user cap)
    preferredWorkoutsPerWeek?: number; // Optional MAX frequency for sport sessions (excludes strength)
    wPrime?: number;        // Joules (Anaerobic Capacity for Cycling/Running)
    runningPowerZones?: Record<string, { min: number; max: number }>; // Calculated from CP
    profilePicture?: string; // URL to athlete profile picture from intervals.icu
    isSupporter?: boolean;   // Added for payment verification
    supportTier?: 'supporter' | 'elite';
    zones?: {
        running: ZoneSet;
        cycling: ZoneSet;
        swimming?: ZoneSet;
    };
    triathlonProgression?: TriathlonProgression;
    planSettings?: PlanSettings; // Relaxed to allow compatibility with Profile.planSettings
    resilienceTier?: 'low' | 'medium' | 'high' | 'elite' | 'competitive' | 'foundation';  // 'foundation' is the neutral starting tier
}

export interface FitnessMetrics {
    // Running metrics
    fiveKTime?: string;    // MM:SS format
    tenKTime?: string;     // MM:SS format
    halfMarathonTime?: string; // HH:MM:SS format
    fitnessContext?: 'rusty' | 'current' | 'improved';
    lthrPace?: number;      // seconds per km
    lthrBpm?: number;       // legacy/shared lactate threshold heart rate
    cyclingLthrBpm?: number;
    runningLthrBpm?: number;

    // Cycling metrics
    ftp?: number;          // functional threshold power (watts)
    criticalPower?: number; // functional threshold power for running (watts)

    // General metrics
    maxHr?: number;
    restingHr?: number;

    // Swimming metrics (for Triathlon)
    /** Critical Swim Speed in seconds per 100m */
    css?: number;
    /** Test times for CSS calculation */
    swimTestTimes?: {
        test200m: number;  // seconds
        test400m: number;  // seconds
    };
    /** Swimming threshold heart rate */
    swimLthr?: number;
}

export interface TrainingGoal {
    id?: string;           // Added for linking to events
    type: 'event' | 'fitness';
    raceDate?: string;
    raceType?: string;      // Made generic to support flexible naming
    priority?: 'A' | 'B' | 'C';
    taperDuration?: number; // Weeks of taper (optional override)
    distance?: number;      // Target distance in km
    eventType?: string;     // Specific event sub-type (e.g., 'Criterium', 'Trail')
    estimatedTimeHours?: number; // Added for later use
    estimatedDurationMinutes?: number; // Added to fix build error
    durationMinutes?: number; // Legacy support
    estimatedTSS?: number; // Manual TSS override
    name?: string; // Added to fix build error
    sport?: AthleteSport; // Added to fix race hub logic
}

export type RaceDistance =
    | '5K' | '10K' | 'HalfMarathon' | 'Marathon'
    | 'Sprint' | 'Olympic' | '70.3' | '140.6'
    | 'Criterium' | 'GranFondo' | 'TimeTrial'
    | string;

export interface PaceBand {
    label: string;
    pacePerKm?: string;            // e.g., "5:30/km"
    pacePerMile?: string;
    targetPower?: number;          // For cycling
    notes: string;
}

export interface PacingStrategy {
    conservative: PaceBand;        // "Safe" - negative split friendly
    moderate: PaceBand;            // Based on training data
    aggressive: PaceBand;          // PR attempt
    selected?: 'conservative' | 'moderate' | 'aggressive';
}

export interface ChecklistItem {
    id: string;
    text: string;
    category: 'Gear' | 'Nutrition' | 'Logistics' | 'Mental';
    daysBeforeRace: number;       // When to show this item
    isChecked: boolean;
    tips?: string;
}

export interface RaceEvent extends TrainingGoal {
    id: string;
    name: string;
    priority: 'A' | 'B' | 'C';
    raceDistance?: RaceDistance;
    targetFinishTime?: number;     // minutes
    pacingStrategy?: PacingStrategy;
    checklist?: ChecklistItem[];
}

// ============================================================================
// ZONE TYPES
// ============================================================================

export interface TrainingZone {
    name: string;
    minPercent: number;
    maxPercent: number;
    description: string;
    color: string;
}

export interface ZoneSet {
    // Standardized Arrays for Visualization & Storage (Source of Truth for Custom Zones)
    power: TrainingZone[]; // e.g., Coggan 7-zone
    heartRate: TrainingZone[]; // e.g., Friel 7-zone
    pace: TrainingZone[]; // e.g., 7-zone based on threshold pace
    swimPace?: TrainingZone[]; // Optional - for swimming

    // Legacy/Lookup maps (optional, can be derived)
    paceZones?: Record<string, { min: number; max: number }>;
    hrZones?: Record<string, { min: number; max: number }>;
    powerZones?: Record<string, { min: number; max: number }>;
    swimPaceZones?: Record<string, { min: number; max: number }>;
}

/**
 * Sport-aware zone collection.
 */
export interface AthleteZones {
    running: ZoneSet;
    cycling: ZoneSet;
    swimming?: ZoneSet;
}

// ============================================================================
// API TYPES
// ============================================================================

export interface IntervalsCredentials {
    athleteId: string;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: number; // timestamp
    scope?: string;
}


export interface StravaConfig {
    clientId: string;
    clientSecret: string;
}

export interface StravaTokens {
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: number; // timestamp in seconds
}

export interface StravaCredentials extends StravaConfig, StravaTokens { }

// ============================================================================
// PLAN TYPES
// ============================================================================

export type PlanMode = 'event' | 'annual';

/**
 * Plan lifecycle states for ATP-07: Plan State Management
 * - draft: Plan is being created/edited, not yet committed
 * - active: Current active training plan
 * - archived: Previous plan, kept for history
 */
export type PlanState = 'draft' | 'active' | 'archived';

export interface PlanConfig {
    mode: PlanMode;
    startDate: string;
    endDate: string;
    events: TrainingGoal[];
}

export interface TrainingPlan {
    id: string;
    createdAt: string;
    updatedAt: string;

    // Plan State (ATP-07)
    state: PlanState;
    name?: string;         // Optional plan name for identification
    archivedAt?: string;   // When the plan was archived

    // Configuration
    config?: PlanConfig;   // Optional for backward compatibility, required for new plans

    // High Level Roadmap (The Guide)
    blocks?: TrainingBlock[];

    athlete: AthleteProfile;
    goal: TrainingGoal;
    metrics: FitnessMetrics;
    philosophy: PhilosophyArchetype;

    // Events
    events: RaceEvent[];

    // Detailed Schedule (The Output)
    weeks: WeekSchedule[];

    availability: WeeklyAvailability;
    progression: {
        startingVolume: number;
        progressionRate: number;  // e.g., 0.10 for 10%

    };
    validationWarnings?: {
        id: string;
        severity: 'error' | 'warning' | 'info';
        category: 'pre' | 'post';
        title: string;
        message: string;
        suggestion?: string;
    }[];
}

// ============================================================================
// UI TYPES
// ============================================================================

export interface DragState {
    workout: ScheduledWorkout | null;
    fromDay: number;
    fromSlot: 'am' | 'pm';
}

export type ViewMode = 'scroll' | 'week' | 'block';

// ============================================================================
// PROFILE & ROLE TYPES (Multi-Tenancy Support)
// ============================================================================

/**
 * User role in the application
 * - athlete: Self-coached athlete managing their own training
 * - coach: Coach managing multiple athletes
 * - admin: Support/debugging role with access to all profiles (future backend auth)
 */
export type UserRole = 'athlete' | 'coach' | 'admin' | 'superadmin';

export type SubscriptionTier = 'free' | 'monthly' | 'yearly' | 'lifetime' | 'donation' | 'referral_credit';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'none';
export type ProSource = 'stripe' | 'referral_reward' | 'admin_grant' | 'donation' | null;

export interface SubscriptionState {
    status: SubscriptionStatus;
    tier: SubscriptionTier;
    currentPeriodEnd: string | null;
    isPro: boolean;
    proSource: ProSource;
}

/** Controls who can access a feature flag, overriding the standard isEnabled/isProOnly logic. */
export type FeatureAccessLevel = 'disabled' | 'admin_only' | 'enabled';

export interface FeatureFlag {
    key: string;
    description?: string;
    isProOnly: boolean;
    minTier?: SubscriptionTier;
    isEnabled: boolean; // Computed based on user
    /** When set, overrides isEnabled + admin bypass for fine-grained access control. */
    accessLevel?: FeatureAccessLevel;
}

export const FEATURE_TRIATHLON = 'triathlon';
export const FEATURE_LOGBOOK = 'logbook';
export const FEATURE_PROGRESS_PAGE = 'progress_page';
export const FEATURE_WEEKLY_CHECKIN = 'weekly_checkin';
export const FEATURE_STRAVA_INTEGRATION = 'strava_integration';
export const FEATURE_ADAPTIVE_ENGINE = 'adaptive_engine';

export interface ReferralState {
    code: string | null;
    referrals: Referral[];
    totalReferrals: number;
    monthsEarned: number;
}

export interface ReferralConfig {
    referrerRewardMonths: number;
    referredRewardMonths: number;
    requirePaidSubscription: boolean;
    minActiveMonths: number;
    isActive: boolean;
}

export interface Referral {
    id: string;
    referredUserId: string;
    status: 'pending' | 'converted';
    createdAt: string;
    rewardClaimed: boolean;
}


/**
 * Relationship between user and profile
 * - self: User's own profile (uses their own API key + their athlete ID)
 * - coached: Athlete managed by coach (uses coach's API key + athlete's ID)
 */
export type ProfileRelation = 'self' | 'coached';

/**
 * Complete athlete profile with all training data
 * Each profile is fully isolated with its own plan, settings, and credentials
 */
export interface Profile {
    // Identity
    id: string;                          // UUID for local reference
    intervalsAthleteId: string;          // Intervals.icu athlete ID (e.g., "i12345")
    relation: ProfileRelation;           // 'self' or 'coached'
    displayName: string;                 // Name shown in profile switcher
    createdAt: string;                   // ISO timestamp
    lastAccessedAt: string;              // ISO timestamp for sorting
    isHydrated?: boolean;                // True once cloud data has been loaded into this profile

    // Auth & Access Control
    email?: string;                      // Linked email
    role?: UserRole;                     // 'athlete', 'coach', 'admin', 'superadmin'
    isPro?: boolean;                     // Pro entitlement
    disabled?: boolean;                  // Admin disable flag
    inviteToken?: string | null;         // For tracking pending invitations
    coachId?: string | null;             // Who is coaching this athlete
    coachName?: string | null;           // Cache coach name for greetings
    connectedAt?: string | null;         // When the athlete connected via OAuth

    // Athlete Data (fully isolated per profile)
    athlete: AthleteProfile;
    metrics: FitnessMetrics;
    goal: TrainingGoal;
    events: RaceEvent[];

    // Availability & Preferences
    availability: WeeklyAvailability;
    longRunDay: number;

    // Training Plan (fully isolated)
    plan: WeekSchedule[];
    planState: PlanState;
    planName?: string;
    planHistory: {
        id: string;
        name: string;
        archivedAt: string;
        weeks: WeekSchedule[];
        blocks: TrainingBlock[];
    }[];
    blocks: TrainingBlock[];
    config: PlanConfig | null;

    // Progression Settings
    progression: {
        startingVolume: number;
        progressionRate: number;

    };
    cyclingProgression: {
        startingTSS: number;
        ftp: number;
        rampRate: number; // CTL/week (3-10 range)
        baselineCTL?: number; // User's starting CTL
        // V2 Engine additions
        ctlSource?: 'intervals_icu' | 'manual' | 'estimated';
        intervalsICUCtl?: number; // Fetched from Intervals.icu
        averageWeeklyHours?: number; // For CTL estimation
    };
    /** Triathlon-specific progression (only for Triathlon athletes) */
    triathlonProgression?: TriathlonProgression;
    planSettings: PlanSettings;
    validationWarnings?: {
        id: string;
        severity: 'error' | 'warning' | 'info';
        category: 'pre' | 'post';
        title: string;
        message: string;
        suggestion?: string;
    }[];
    profilePicture?: string; // URL to athlete profile picture from intervals.icu
    isSupporter?: boolean;
    supportTier?: 'supporter' | 'elite';
    stats?: UserStats;
    gamification?: GamificationState;
}

export type WorkoutCompletionStatus = 'planned' | 'done' | 'missed' | 'skipped_sick' | 'skipped_life';

export type AthleteLevel = 'Everyday' | 'Dedicated' | 'Consistent' | 'Machine' | 'Legendary';

export interface UserStats {
    currentStreak: number;      // Consecutive weeks with >80% adherence
    longestStreak: number;
    totalWeeksCompleted: number;
    consistencyScore: number;   // Rolling 4-week adherence percentage
    athleteLevel: AthleteLevel;
    lastConsistencyCheck?: string; // ISO date of last check
}

/**
 * Summary info for profile switcher (lightweight)
 */
export interface ProfileSummary {
    id: string;
    intervalsAthleteId: string;
    displayName: string;
    relation: ProfileRelation;
    sport: AthleteSport;
    lastAccessedAt: string;
    profilePicture?: string;
    inviteToken?: string | null;
    isSupporter?: boolean;
    connectedAt?: string | null;
}

// ============================================================================
// TRIATHLON PROGRESSION TYPES
// ============================================================================

export type TriathlonProgressionLevel =
    | 'base'
    | 'regular'
    | 'performance'
    | 'competitive';

export type TriathlonRunningProgressionMode = 'distance' | 'load';
export type TriathlonCyclingProgressionMode = 'time' | 'load' | 'hybrid';
export type TriathlonSwimmingProgressionMode = 'time';

export interface TriathlonRunningProgressionSettings {
    mode: TriathlonRunningProgressionMode;
    progressionLevel: TriathlonProgressionLevel;
    baselineDistanceKm?: number;
    baselineLoad?: number;
}

export interface TriathlonCyclingProgressionSettings {
    mode: TriathlonCyclingProgressionMode;
    progressionLevel: TriathlonProgressionLevel;
    baselineHours?: number;
    baselineLoad?: number;
}

export interface TriathlonSwimmingProgressionSettings {
    mode: TriathlonSwimmingProgressionMode;
    progressionLevel: TriathlonProgressionLevel;
    baselineMinutes?: number;
}

export interface TriathlonProgressionSettings {
    totalBaselineHours?: number;
    running: TriathlonRunningProgressionSettings;
    cycling: TriathlonCyclingProgressionSettings;
    swimming: TriathlonSwimmingProgressionSettings;
}

export interface TriathlonWeeklyTargets {
    totalHours: number;
    phaseCapHours: number;
    primaryLimiter?: string;
    swimming: {
        mode: TriathlonSwimmingProgressionMode;
        minutes: number;
        sessions: number;
        share: number;
        tss: number;
    };
    cycling: {
        mode: TriathlonCyclingProgressionMode;
        hours: number;
        share: number;
        tss: number;
    };
    running: {
        mode: TriathlonRunningProgressionMode;
        hours: number;
        share: number;
        distanceKm: number;
        rTSS: number;
    };
    strength: {
        hours: number;
        share: number;
        tss: number;
    };
}

/**
 * Triathlon-specific progression settings.
 * Tracks CTL separately for each discipline with ECO-normalized composite.
 */
export interface TriathlonProgression {
    /** Swimming CTL (Chronic Training Load) */
    swimCTL: number;
    /** Cycling CTL */
    bikeCTL: number;
    /** Running CTL */
    runCTL: number;
    /** ECO-normalized composite CTL */
    compositeCTL: number;

    /** Training focus levels for each discipline */
    disciplineFocus?: {
        swim: 'maintenance' | 'standard' | 'prioritized' | 'mastery';
        bike: 'maintenance' | 'standard' | 'prioritized' | 'mastery';
        run: 'maintenance' | 'standard' | 'prioritized' | 'mastery';
        strength: 'maintenance' | 'standard' | 'prioritized' | 'mastery';
    };

    /** Swimming metrics */
    swimmingMetrics: {
        css: number;            // Critical Swim Speed (sec/100m)
        weeklySwimTSS: number;  // Target weekly swim TSS
        swimFrequency: number;  // Sessions per week
        openWaterAccess?: boolean;
    };

    /** Preferred maximum sessions per week for each discipline */
    sessionCaps?: {
        swim: number | null;
        bike: number | null;
        run: number | null;
        strength: number | null;
    };

    /** Triathlon-only weekly rhythm preferences */
    weeklyRhythm?: {
        /** Deprecated UI-facing input kept for backwards compatibility */
        frequencyTargets: {
            swim: number | null;
            bike: number | null;
            run: number | null;
            strength: number | null;
        };
        preferredDays: {
            swim: number[];
            bike: number[];
            run: number[];
            strength: number[];
        };
        anchorDays: {
            longBike: number[];
            longRun: number[];
        };
        fixedSessions: {
            swimLessons: Array<{
                day: number;
                slot: 'am' | 'pm';
            }>;
        };
        flexibilityMode: 'strict' | 'balanced' | 'aggressive';
    };

    /** Discipline distribution as percentage of weekly normalized load */
    disciplineDistribution: {
        swim: number;       // ~20% for most distances
        bike: number;       // ~50% for most distances
        run: number;        // ~25% for most distances
        strength: number;   // ~5%
    };

    /** Target triathlon distance */
    targetDistance?: TriathlonDistance;

    /** Ramp rate for composite CTL (CTL points per week) */
    rampRate: number;  // 4-8 range for triathlon

    /** Discipline-native progression controls for triathlon ATP generation */
    progressionSettings?: TriathlonProgressionSettings;

    /** Most recently resolved discipline targets (ephemeral/UI/debug friendly) */
    weeklyTargets?: TriathlonWeeklyTargets;
}

/**
 * Default triathlon discipline distribution by race distance.
 */
export const TRIATHLON_DISTRIBUTIONS: Record<TriathlonDistance, TriathlonProgression['disciplineDistribution']> = {
    super_sprint: { swim: 25, bike: 40, run: 30, strength: 5 },  // More swim focus for short course
    sprint: { swim: 22, bike: 45, run: 28, strength: 5 },
    olympic: { swim: 20, bike: 50, run: 25, strength: 5 },
    half_ironman: { swim: 18, bike: 52, run: 25, strength: 5 },
    full_ironman: { swim: 15, bike: 55, run: 25, strength: 5 },  // Bike dominant for long course
} as const;

/**
 * Triathlon distance specifications.
 */
export const TRIATHLON_DISTANCE_SPECS: Record<TriathlonDistance, TriathlonDistanceSpec> = {
    super_sprint: { swim: 400, bike: 10, run: 2.5, typicalHoursMin: 0.5, typicalHoursMax: 1, weeklyHoursMin: 5, weeklyHoursMax: 7 },
    sprint: { swim: 750, bike: 20, run: 5, typicalHoursMin: 1, typicalHoursMax: 1.5, weeklyHoursMin: 6, weeklyHoursMax: 8 },
    olympic: { swim: 1500, bike: 40, run: 10, typicalHoursMin: 2, typicalHoursMax: 3, weeklyHoursMin: 8, weeklyHoursMax: 12 },
    half_ironman: { swim: 1900, bike: 90, run: 21.1, typicalHoursMin: 4, typicalHoursMax: 7, weeklyHoursMin: 10, weeklyHoursMax: 14 },
    full_ironman: { swim: 3800, bike: 180, run: 42.2, typicalHoursMin: 8, typicalHoursMax: 17, weeklyHoursMin: 12, weeklyHoursMax: 20 },
} as const;

// ============================================================================
// MACRO PLANNING TYPES
// ============================================================================

export interface MacroWeek {
    weekNumber: number;
    startDate?: string; // ISO date string
    phase: PhaseType;
    volume: number;        // Distance (km) OR TSS (Cycling/Triathlon)
    volumeUnit?: 'km' | 'TSS' | 'hours';
    longRun: number;       // Distance (km) OR Duration (min)
    longRideDuration?: number; // Duration (min) - Primarily for Triathlon (PHASE-2)
    tss?: number;          // Explicit TSS target
    duration?: number;     // Explicit Duration target (minutes)
    isRecovery: boolean;
    recoveryTrigger?: RecoveryTrigger;
    philosophy?: PhilosophyArchetype;
    rulesTrace?: WeekRulesTrace;
    isRaceWeek?: boolean;
    eventName?: string;    // Added for SeasonPlanner CRUD (FEAT-10)
    eventPriority?: 'A' | 'B' | 'C'; // Added for SeasonPlanner CRUD (FEAT-10)
    weeksToRace?: number; // Distance to primary A-race (My implementation)
    focus: string;
    capacity?: number;
    blockNumber?: number; // Added for Unified Block Display (UX-12)

    // === ATP Engine v2 Fields ===
    /** Position within cycle (1-based) */
    cycleWeekPosition?: number;
    /** Cycle number (1-based) */
    cycleNumber?: number;
    /** Cycle pattern in use (e.g., '3:1') */
    cyclePattern?: '2:1' | '3:1' | '4:1' | 'custom';
    /** Whether this week is at the effective cap */
    isAtCap?: boolean;
    /** Warning or info message for this week (legacy, use flags/concerns) */
    warning?: string;
    /** Week within the current phase (reset to 1 on phase change) */
    phaseRelativeWeek?: number;
    /** Constraint status applied by the engine */
    constraint?: 'hours_capped' | 'ramp_limited' | 'volume_governor';

    // === ATP Core v3: Validation Output ===
    /** Source of phase assignment: proposed by engine or set by user */
    phaseSource?: 'proposed' | 'user_set';
    /** If true, engine must not suggest structural changes to this week */
    locked?: boolean;
    /** Hard warnings: unsafe or contradictory configurations */
    flags?: string[];
    /** Soft warnings: likely suboptimal choices */
    concerns?: string[];
    /** Improvement opportunities (never auto-applied) */
    suggestions?: string[];

    // === ATP Core v3: Pattern Detection Heuristics ===
    /** Load significantly lower than recent weeks */
    isRecoveryLike?: boolean;
    /** Shows step-down pattern approaching event */
    isTaperLike?: boolean;
    /** Near upper end of recent load distribution */
    isPeakLike?: boolean;
    /** Week-over-week volume change (km or TSS) */
    deltaVolume?: number;
    /** Week-over-week percentage change */
    deltaPercent?: number;

    /** Estimated Running TSS for the week */
    rTSS?: number;
    rtssTier?: RtssTier;

    // === Cycling Pro View Fields ===
    /** Chronic Training Load (CTL/Fitness) at end of week */
    ctl?: number;
    /** Acute Training Load (ATL/Fatigue) at end of week */
    atl?: number;
    /** Training Stress Balance (Form) at end of week */
    tsb?: number;
    /** Intensity Factor (IF) used for the week */
    intensityFactor?: number;
    /** Which TSS limiter was active (for Pro View display) */
    activeLimiter?: LimiterType;
    /** Current ACWR value for traffic-light visualization */
    acwrValue?: number;
    /** Safety intervention details for educational tooltips */
    safetyIntervention?: SafetyIntervention;
    /** Rationale for scheduler decisions in this week */
    rationale?: string;
    /** Type of long session in this week */
    longSessionType?: 'steady' | 'structured';
    /** Specific instruction for the long ride (e.g. "Add 2x15m Tempo") */
    longRunInstruction?: string;

    // === Shared Metrics (Compatibility with WeekSchedule) ===
    startingCTL?: number;
    endCTL?: number;
    endTSB?: number;
    endATL?: number;
    projectedMetrics?: {
        ctl: number;
        atl: number;
        tsb: number;
        startCTL?: number;
    };

    /** Additional properties used in ATP Engine logic */
    targetVolume?: number;
    notes?: string;
    triathlonTargets?: TriathlonWeeklyTargets;
    [key: string]: unknown; // Allow for extensibility and legacy fields
}

/**
 * Configuration for the ATP Engine, loaded dynamically from the database.
 */
export interface EngineConfig {
    sawtooth_w1: number;
    sawtooth_w2: number;
    sawtooth_w3: number;
    recovery_factor: number;
    foundation_ramp: number;
    [key: string]: number; // Allow extensibility
}

// ============================================================================
// ACTIVITY & MATCHING TYPES
// ============================================================================

export interface IntervalsActivity {
    id: string;
    start_date_local: string; // ISO string
    name: string;
    distance: number;
    moving_time: number;
    elapsed_time: number;
    total_elevation_gain: number;
    type: string;
    icu_training_load?: number;
    icu_intensity?: number;
    average_heartrate?: number;
    max_heartrate?: number;
    average_speed?: number;
    calories?: number;
    description?: string;
    source: 'strava' | 'garmin' | 'manual' | 'other';
    external_id?: string;
    map?: {
        summary_polyline: string;
    };
    device_name?: string;
    average_cadence?: number;
    average_temp?: number;
    has_heartrate: boolean;
    max_speed?: number;
    pr_count?: number;
    start_latlng?: [number, number];
    end_latlng?: [number, number];
    strava_id?: string;
    intervals_id?: string;
}

export type ComplianceStatus = 'compliant' | 'non_compliant' | 'unplanned' | 'partial';

/**
 * Matches a planned ScheduledWorkout (via instanceId) to a raw Activity
 */
export interface ActivityMatch {
    id: string;
    user_id?: string;
    plan_id?: string;
    workout_instance_id: string; // The stable ID linkage
    activity_id: string;         // The raw activity ID
    compliance_score: number;    // 0-100
    status: ComplianceStatus;
    is_manual_association: boolean;
    created_at?: string;
    updated_at?: string;

    // Optional joined data for UI convenience
    activity?: IntervalsActivity;
    workout?: ScheduledWorkout;
}

// ============================================================================
// ADAPTATION ENGINE TYPES (Phase 3)
// ============================================================================

/** Numeric priority derived from DaySlot.priority for adaptation decision logic. */
export type SessionPriority = 1 | 2 | 3 | 4 | 5;

/**
 * Maps DaySlot.priority → numeric priority for adaptation decision logic.
 * 1 = KEY (long session), 2 = BIG_ROCK (quality/intervals),
 * 3 = SUPPORT (or undefined), 4 = FILLER, 5 = FILL
 */
export const SESSION_PRIORITY_MAP: Record<NonNullable<DaySlot['priority']>, SessionPriority> = {
    KEY: 1,
    BIG_ROCK: 2,
    SUPPORT: 3,
    RECOVERY: 3,
    BLOCKED: 5,
    FILLER: 4,
    FILL: 5,
} as const;

/** Flags that describe specific stimulus patterns detected in a completed week. */
export type StimulusFlag =
    | 'PERFECT_WEEK'           // ≥85% compliance, all key sessions intact
    | 'INTENSITY_CREEP'        // TSS met but all/mostly low-intensity
    | 'VOLUME_DEFICIT'         // Significant aerobic volume undershoot
    | 'CONSECUTIVE_LOW'        // Two or more consecutive low compliance weeks
    | 'PROXY_ILLNESS_OR_INJURY'// Low compliance + reschedules + shortened sessions
    | 'OVERTRAINING_RISK'      // TSS significantly above target (>120%)
    | 'LONG_SESSION_MISSED'    // Priority 1 session was not completed
    | 'QUALITY_SESSION_MISSED' // Priority 2 session was not completed
    | 'ADEQUATE';              // Normal week, no special flags

/** Weekly stimulus credit — computed from completed activities vs plan. */
export interface WeeklyStimulusCredit {
    weekId: string;                    // ISO start date of the week
    plannedTSS: number;
    actualTSS: number;
    tssComplianceRatio: number;        // actualTSS / plannedTSS
    aerobicVolume: number;             // km or hours depending on sport
    highIntensityRatio: number;        // Fraction of time in zone 4+
    longSessionCompleted: boolean;     // Priority 1 session done
    keySessionCompleted: boolean;      // Priority 2 session done
    overallComplianceRatio: number;    // Weighted composite 0-1
    rescheduledCount: number;
    shortenedCount: number;
    flags: StimulusFlag[];
}

/** What kind of plan change is proposed. */
export type AdaptationTrigger =
    | 'LOW_COMPLIANCE'          // <60% overall
    | 'MODERATE_UNDERSHOOT'     // 60-74%
    | 'SLIGHT_UNDERSHOOT'       // 75-84%
    | 'ON_TRACK'                // 85-100%
    | 'OVERTRAINING'            // >120% TSS
    | 'REDUCE_RAMP'             // Two consecutive low weeks
    | 'CAP_LOAD'                // Overtraining risk
    | 'MISSED_KEY_SESSION'      // Priority 1 or 2 missed
    | 'PROXY_ILLNESS';          // Illness/injury proxy detected

/** A single session change within a plan mutation. */
export interface SessionChange {
    sessionId: string;
    action: 'REMOVE' | 'SHORTEN' | 'REPLACE' | 'RESCHEDULE' | 'ADD';
    originalDuration?: number;   // minutes
    newDuration?: number;        // minutes
    rationale: string;
}

/** Describes how the plan should be mutated. */
export interface PlanMutation {
    type: 'REDUCE_VOLUME' | 'REDUCE_INTENSITY' | 'ADD_RECOVERY' | 'MAINTAIN' | 'INCREASE_VOLUME' | 'CAP_LOAD';
    weekOffset: number;          // 0 = current week, 1 = next week, etc.
    volumeAdjustmentPercent?: number; // e.g. -20 means reduce 20%
    sessionChanges: SessionChange[];
    description: string;
}

/** How a missed session should be handled (real-time, not weekly). */
export interface SessionAdaptation {
    originalSessionId: string;
    recommendation: 'REDISTRIBUTE' | 'DROP' | 'DEFER';
    redistributedTo?: string[];   // Day ISO strings
    rationale: string;
    autoApply: boolean;
}

/** A proposed plan mutation surfaced to the athlete. */
export interface AdaptationProposal {
    id: string;
    userId: string;
    planId: string;
    weekId: string;                // ISO start date of evaluated week
    triggeredAt: string;           // ISO timestamp
    status: 'PENDING' | 'ACCEPTED' | 'DISMISSED' | 'AUTO_APPLIED';
    trigger: AdaptationTrigger;
    proposedMutation: PlanMutation;
    rationale: string;
    optionA: PlanMutation;         // Conservative
    optionB: PlanMutation;         // Continue (no change)
    appliedAt?: string;
    appliedOption?: 'A' | 'B';
    previousState?: unknown;       // Snapshot of plan before mutation
}

/** Nudge severity levels for UI presentation. */
export type NudgeSeverity = 'INFO' | 'SUGGESTION' | 'ACTION_REQUIRED';

/** A non-punitive nudge surfaced to the athlete in the UI. */
export interface AdaptationNudge {
    id: string;
    userId: string;
    proposalId: string;
    message: string;
    severity: NudgeSeverity;
    displayedAt?: string;
    dismissedAt?: string;
    createdAt: string;
    // Convenience join
    proposal?: AdaptationProposal;
}

/** Audit record of a plan mutation. */
export interface AdaptationAuditLog {
    id: string;
    proposalId: string;
    userId: string;
    planId: string;
    mutationType: string;
    previousState: unknown;
    newState: unknown;
    appliedAt: string;
}

/** Tracks whether a week has been evaluated by the adaptation engine. */
export interface WeekEvaluationLog {
    id: string;
    userId: string;
    weekId: string;
    evaluatedAt: string;
    status: 'COMPLETED' | 'PENDING_RETRY';
    retryCount: number;
}
